import { createContext, useContext } from 'react';
import { Avatar } from '../../components/shared/Avatar';
import type { AvatarProps } from '../../components/shared/Avatar/Avatar';
import type { CalendarEventDto } from '../../api/calendar';

/**
 * 일정 작성자 아바타 (2026-07-10) — "누가 등록한 일정인지" 를 프로필사진으로 매핑한다.
 *
 * 팀 캘린더 일정에만 붙인다. 개인 일정은 전부 본인이 만든 것이라 아바타가 정보를 주지 않는다.
 * 판정에 필요한 "팀 캘린더 id 집합" 은 CalendarPage 가 한 번 조회해 context 로 내려준다 —
 * MonthView/TimeGridView/DayPopover/EventDetailModal 에 prop 을 새로 뚫지 않기 위함(회귀 안전).
 *
 * context 기본값이 빈 집합이라, provider 없이 렌더되는 기존 테스트·스토리는 아바타 없이 그대로 동작한다.
 */

const EMPTY: ReadonlySet<string> = new Set();

const TeamCalendarIdsContext = createContext<ReadonlySet<string>>(EMPTY);

export function TeamCalendarIdsProvider({
  ids,
  children,
}: {
  ids: ReadonlySet<string>;
  children: React.ReactNode;
}) {
  return <TeamCalendarIdsContext.Provider value={ids}>{children}</TeamCalendarIdsContext.Provider>;
}

/** 이 일정이 팀 캘린더 소속인가 — 작성자 아바타 노출 조건. */
export function useIsTeamEvent(event: CalendarEventDto): boolean {
  const teamCalendarIds = useContext(TeamCalendarIdsContext);
  return event.calendarId != null && teamCalendarIds.has(event.calendarId);
}

export interface EventCreatorAvatarProps {
  event: CalendarEventDto;
  size?: AvatarProps['size'];
  className?: string;
}

/**
 * 조건 미충족(개인 일정 / 구버전 응답이라 creator 없음)이면 아무것도 렌더하지 않는다 —
 * 호출측이 분기하지 않아도 되도록 null 반환으로 흡수한다.
 */
export function EventCreatorAvatar({ event, size = 'xxs', className }: EventCreatorAvatarProps) {
  const isTeamEvent = useIsTeamEvent(event);
  const creator = event.creator;
  if (!creator || !isTeamEvent) return null;
  return (
    <span className={className} title={creator.name}>
      <Avatar userId={Number(creator.id)} name={creator.name} src={creator.avatarUrl} size={size} />
    </span>
  );
}
